/**
 * HorariosView – Gestión de plantillas de horario semanal (template-based).
 *
 * Pestañas:
 *   Plantillas  – Grilla L-D × colaboradores, sin navegación de fechas.
 *                 Cada celda muestra la plantilla permanente del colaborador para ese día.
 *   Cobertura   – Gantt configurable para una fecha concreta (resuelve por día de semana).
 *
 * @module presentation/views/HorariosView
 */

import { PlantillasApiClient }  from '../../infrastructure/api/PlantillasApiClient.js';
import { unwrap } from '../../domain/value-objects/CrudResult.js';
import { ColaboradorApiClient } from '../../infrastructure/api/ColaboradorApiClient.js';
import { BusinessApiClient }   from '../../infrastructure/api/BusinessApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { renderTurnos } from '../shared/NxTurnos.js';
import { avisar } from '../shared/nxDialogo.js';

// ---------------------------------------------------------------------------
// Constantes y helpers
// ---------------------------------------------------------------------------

const DIAS_ISO = [1, 2, 3, 4, 5, 6, 7];
const DIA_LABEL = { 1:'Lunes', 2:'Martes', 3:'Miércoles', 4:'Jueves', 5:'Viernes', 6:'Sábado', 7:'Domingo' };
const DIA_SHORT = { 1:'L', 2:'M', 3:'X', 4:'J', 5:'V', 6:'S', 7:'D' };
const BLOQUE_STEP = 30;

function minFromHHMM(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

function hhmm(totalMin) {
  const h = Math.floor(totalMin / 60).toString().padStart(2, '0');
  const m = (totalMin % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

/** Clase CSS de bloque Gantt para un minuto absoluto dado un horario (plantilla). */
function bloqueClass(bMin, p) {
  if (!p) return 'bloque-vacio';
  if (p.esDescanso) return 'bloque-descanso';
  const bMax = bMin + BLOQUE_STEP;
  const ent  = minFromHHMM(p.horaEntrada);
  const sal  = minFromHHMM(p.horaSalida);
  const iAlm = minFromHHMM(p.horaInicioAlmuerzo);
  const fAlm = minFromHHMM(p.horaFinAlmuerzo);
  if (ent == null || sal == null) return 'bloque-vacio';
  if (bMin >= sal || bMax <= ent)  return 'bloque-vacio';
  if (iAlm != null && fAlm != null && bMin >= iAlm && bMax <= fAlm) return 'bloque-almuerzo';
  return 'bloque-trabajo';
}

/** Clase CSS de bloque para detectar huecos (nadie cubre en horario de apertura). */
function bloqueHueco(bMin, aperDesde, aperHasta, plantillas) {
  if (aperDesde == null || aperHasta == null) return false;
  const bMax = bMin + BLOQUE_STEP;
  if (bMin >= aperHasta || bMax <= aperDesde) return false;
  // hay hueco si ninguna plantilla cubre este bloque
  return plantillas.every(p => bloqueClass(bMin, p) !== 'bloque-trabajo');
}

// ---------------------------------------------------------------------------
// Render plantillas grid (L-D estático, sin navegación de fechas)
// ---------------------------------------------------------------------------

function renderGrillaPlantillas(plantillas, colaboradores) {
  if (!colaboradores.length) {
    return '<div class="empty-state"><p>No hay colaboradores activos. Créalos primero en el módulo Colaboradores.</p></div>';
  }

  // map: colaboradorId|diaSemana → plantilla
  const map = {};
  plantillas.forEach(p => { map[`${p.colaboradorId}|${p.diaSemana}`] = p; });

  const encabezado = `
    <div class="horario-grid-header">
      <div class="horario-col-nombre">Colaborador</div>
      ${DIAS_ISO.map(d => `<div class="horario-col-dia">${DIA_LABEL[d]}</div>`).join('')}
    </div>`;

  const filas = colaboradores.map(c => {
    const celdas = DIAS_ISO.map(dia => {
      const p = map[`${c.id}|${dia}`];
      return `<div class="horario-celda" data-colab="${c.id}" data-dia="${dia}">${renderCelda(c, dia, p)}</div>`;
    }).join('');
    return `
      <div class="horario-fila">
        <div class="horario-col-nombre">
          <div class="horario-nombre">${c.nombreCompleto || c.nombre_completo || ''}</div>
          <div class="horario-cargo">${c.cargo || ''}</div>
        </div>
        ${celdas}
      </div>`;
  }).join('');

  return `
    <div class="horario-grid-wrapper">
      ${encabezado}
      <div class="horario-grid-body">${filas}</div>
    </div>
    <div class="horario-leyenda">
      <span class="leyenda-item"><span class="leyenda-color bloque-trabajo"></span>Trabajo</span>
      <span class="leyenda-item"><span class="leyenda-color bloque-almuerzo"></span>Almuerzo</span>
      <span class="leyenda-item"><span class="leyenda-color bloque-descanso" style="background:#6e4aaa"></span>Descanso</span>
      <span class="leyenda-item"><span class="leyenda-color bloque-vacio"></span>Sin asignar</span>
    </div>`;
}

function renderCelda(colaborador, dia, plantilla) {
  const nombre = colaborador.nombreCompleto || colaborador.nombre_completo || '';
  if (!plantilla) {
    return `
      <button class="btn-celda-add"
        data-colab-id="${colaborador.id}"
        data-colab-nombre="${nombre}"
        data-dia="${dia}"
        title="Asignar plantilla para ${DIA_LABEL[dia]}">+</button>`;
  }
  if (plantilla.esDescanso) {
    return `
      <div class="celda-horario celda-descanso" data-plantilla-id="${plantilla.id}"
        data-colab-id="${colaborador.id}" data-colab-nombre="${nombre}" data-dia="${dia}"
        style="background:rgba(110,74,170,0.18);border-left:3px solid #6e4aaa">
        <div class="celda-horas" style="color:#b8a0e8">💤 Descanso</div>
        <div class="celda-totales" style="color:#8e8e93">dia libre</div>
      </div>`;
  }
  const hrs = plantilla.horasProgramadas != null ? `${plantilla.horasProgramadas}h` : '';
  return `
    <div class="celda-horario" data-plantilla-id="${plantilla.id}"
      data-colab-id="${colaborador.id}" data-colab-nombre="${nombre}" data-dia="${dia}">
      <div class="celda-horas">${plantilla.horaEntrada} – ${plantilla.horaSalida}</div>
      ${plantilla.horaInicioAlmuerzo ? `<div class="celda-alm">🍽 ${plantilla.horaInicioAlmuerzo}</div>` : ''}
      <div class="celda-totales">${hrs}</div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Modal agregar / editar plantilla
// ---------------------------------------------------------------------------

function showPlantillaModal(opts = {}) {
  const {
    businessId,
    colaboradores = [],
    colabId = '',
    colabNombre = '',
    diaSemanaPresel = [],   // array de días ISO (1-7) pre-seleccionados
    plantilla = null,       // datos actuales para edición
    onSaved,
  } = opts;

  document.getElementById('nexo-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'nexo-modal-overlay';
  overlay.className = 'modal-overlay';

  const esEdicion = !!plantilla;
  const ent  = plantilla?.horaEntrada       || '08:00';
  const sal  = plantilla?.horaSalida        || '17:00';
  const iAlm = plantilla?.horaInicioAlmuerzo || '';
  const fAlm = plantilla?.horaFinAlmuerzo   || '';
  const esDescansoInit = !!plantilla?.esDescanso;

  // Opciones de colaborador
  const colabOpts = colaboradores.map(c =>
    `<option value="${c.id}" ${c.id === colabId ? 'selected' : ''}>${c.nombreCompleto || c.nombre_completo}</option>`
  ).join('');

  // Checkboxes de días: en edición solo el día actual está fijado (disabled); los demás son seleccionables para copiar
  const diasChecks = DIAS_ISO.map(d => {
    const esDiaActual = esEdicion && d === plantilla?.diaSemana;
    const checked     = diaSemanaPresel.includes(d) || esDiaActual;
    const disabled    = esDiaActual ? 'disabled' : '';
    return `
      <label class="hm-day-check ${checked ? 'hm-day-active' : ''}" title="${esDiaActual ? 'Día actual (fijado)' : ''}">
        <input type="checkbox" name="dia_semana" value="${d}" ${checked ? 'checked' : ''} ${disabled}>
        ${DIA_SHORT[d]}
      </label>`;
  }).join('');

  overlay.innerHTML = `
    <div class="modal-box horario-modal-box" role="dialog" aria-modal="true">
      <div class="modal-header">
        <h3 class="modal-title">${esEdicion ? '✏️ Editar plantilla' : '📅 Nueva plantilla'}</h3>
        <button class="modal-close" id="hm-close">✕</button>
      </div>

      <div class="hm-info-banner">
        Las plantillas son <strong>permanentes</strong>: se aplican cada semana hasta que las cambies.
      </div>

      <div class="modal-body hm-body">
        ${!esEdicion ? `
        <div class="hm-section">
          <label class="hm-label">Colaborador</label>
          <select id="hm-colab" class="form-control">
            <option value="">— Seleccionar —</option>
            ${colabOpts}
          </select>
        </div>` : `
        <div class="hm-section">
          <p class="hm-colab-nombre">👤 ${colabNombre} — <strong>${DIA_LABEL[plantilla.diaSemana]}</strong></p>
        </div>`}

        <div class="hm-section">
          <label class="hm-label">Días de la semana</label>
          <div class="hm-days-row">${diasChecks}</div>
          <p class="hm-hint">
            ${esEdicion
              ? 'El día actual está fijado. Marca otros días para copiar este horario a esos días también.'
              : 'Puedes seleccionar varios días; se creará una plantilla por cada uno.'}
          </p>
        </div>

        <div class="hm-section">
          <label class="hm-label" style="display:flex;align-items:center;gap:8px;cursor:pointer">
            <input type="checkbox" id="hm-descanso" ${esDescansoInit ? 'checked' : ''}
              style="width:16px;height:16px;accent-color:#6e4aaa">
            <span>💤 Este dia es de <strong>descanso</strong> (no trabaja)</span>
          </label>
          <p class="hm-hint" style="margin-top:4px">Al marcar descanso, las horas se ignoran. El colaborador no penaliza por no presentarse ese dia.</p>
        </div>

        <div class="hm-section hm-jornada-section" style="${esDescansoInit ? 'opacity:0.4;pointer-events:none' : ''}">
          <label class="hm-label">Jornada laboral</label>
          <div class="hm-row-2">
            <div class="hm-field">
              <label class="hm-sublabel">Entrada</label>
              <input type="time" id="hm-entrada" class="form-control" value="${ent}">
            </div>
            <div class="hm-field">
              <label class="hm-sublabel">Salida</label>
              <input type="time" id="hm-salida" class="form-control" value="${sal}">
            </div>
          </div>
        </div>

        <div class="hm-section hm-almuerzo-section" style="${esDescansoInit ? 'opacity:0.4;pointer-events:none' : ''}">
          <label class="hm-label">Almuerzo <span class="hm-hint">(opcional)</span></label>
          <div class="hm-row-2">
            <div class="hm-field">
              <label class="hm-sublabel">Inicio</label>
              <input type="time" id="hm-alm-ini" class="form-control" value="${iAlm}">
            </div>
            <div class="hm-field">
              <label class="hm-sublabel">Fin</label>
              <input type="time" id="hm-alm-fin" class="form-control" value="${fAlm}">
            </div>
          </div>
        </div>

        <div class="hm-section">
          <label class="hm-label">Notas</label>
          <textarea id="hm-notas" class="form-control" rows="2" placeholder="Observaciones opcionales">${plantilla?.notas || ''}</textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-ghost" id="hm-cancel">Cancelar</button>
        <button class="btn btn-primary" id="hm-save">
          ${esEdicion ? 'Actualizar' : 'Guardar plantilla'}
        </button>
      </div>
    </div>`;

  document.body.appendChild(overlay);

  overlay.querySelector('#hm-close').onclick  = () => overlay.remove();
  overlay.querySelector('#hm-cancel').onclick = () => overlay.remove();
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  // --- días checkbox toggle visual (usa clase CSS .hm-day-active)
  overlay.querySelectorAll('.hm-day-check input').forEach(cb => {
    cb.addEventListener('change', () => {
      cb.closest('.hm-day-check').classList.toggle('hm-day-active', cb.checked);
    });
  });

  // --- toggle es_descanso: deshabilita campos de horas cuando es descanso
  const $descanso = overlay.querySelector('#hm-descanso');
  if ($descanso) {
    $descanso.addEventListener('change', () => {
      const disabled = $descanso.checked;
      const secciones = overlay.querySelectorAll('.hm-jornada-section, .hm-almuerzo-section');
      secciones.forEach(s => {
        s.style.opacity = disabled ? '0.4' : '';
        s.style.pointerEvents = disabled ? 'none' : '';
      });
    });
  }

  overlay.querySelector('#hm-save').onclick = async () => {
    const esDescanso = overlay.querySelector('#hm-descanso')?.checked || false;
    const entrada = overlay.querySelector('#hm-entrada').value;
    const salida  = overlay.querySelector('#hm-salida').value;
    const almIni  = overlay.querySelector('#hm-alm-ini').value;
    const almFin  = overlay.querySelector('#hm-alm-fin').value;
    const notas   = overlay.querySelector('#hm-notas').value;

    if (!esDescanso && (!entrada || !salida)) {
      avisar('Hora de entrada y salida son obligatorias (a menos que marques como dia de descanso).', { tono: 'error' });
      return;
    }

    const btn = overlay.querySelector('#hm-save');
    btn.disabled = true;
    btn.textContent = 'Guardando…';

    try {
      const payloadBase = esDescanso
        ? {
            esDescanso: true,
            horaEntrada: null,
            horaSalida: null,
            horaInicioAlmuerzo: null,
            horaFinAlmuerzo: null,
            notas: notas || null,
          }
        : {
            esDescanso: false,
            horaEntrada: entrada,
            horaSalida: salida,
            horaInicioAlmuerzo: almIni || null,
            horaFinAlmuerzo:    almFin || null,
            notas: notas || null,
          };

      if (esEdicion) {
        unwrap(await PlantillasApiClient.update(businessId, plantilla.id, payloadBase), 'la API rechazo la plantilla');
        const diasExtra = [...overlay.querySelectorAll('input[name=dia_semana]:checked')]
          .map(cb => Number(cb.value))
          .filter(d => d !== plantilla.diaSemana);
        if (diasExtra.length) {
          const resExtra = await Promise.all(diasExtra.map(dia => PlantillasApiClient.upsert(businessId, {
            colaboradorId: plantilla.colaboradorId,
            diaSemana:     dia,
            ...payloadBase,
          })));
          resExtra.forEach(r => unwrap(r, 'la API rechazo una plantilla de dia extra'));
        }
      } else {
        const colabSel = colabId || overlay.querySelector('#hm-colab')?.value;
        if (!colabSel) { avisar('Selecciona un colaborador.', { tono: 'error' }); btn.disabled = false; btn.textContent = 'Guardar plantilla'; return; }

        const diasSel = [...overlay.querySelectorAll('input[name=dia_semana]:checked')].map(cb => Number(cb.value));
        if (!diasSel.length) { avisar('Selecciona al menos un día.', { tono: 'error' }); btn.disabled = false; btn.textContent = 'Guardar plantilla'; return; }

        const calls = diasSel.map(dia => PlantillasApiClient.upsert(businessId, {
          colaboradorId: colabSel,
          diaSemana:     dia,
          ...payloadBase,
        }));
        (await Promise.all(calls)).forEach(r => unwrap(r, 'la API rechazo una plantilla'));
      }
      overlay.remove();
      onSaved?.();
    } catch (err) {
      console.error(err);
      avisar('Error guardando la plantilla: ' + (err.message || err), { tono: 'error' });
      btn.disabled = false;
      btn.textContent = esEdicion ? 'Actualizar' : 'Guardar plantilla';
    }
  };
}

// ---------------------------------------------------------------------------
// Cobertura – Vista semanal + vista diaria
// ---------------------------------------------------------------------------

async function loadCobertura(container, businessId) {
  const hoy = new Date().toISOString().slice(0, 10);

  // Horario de apertura del negocio desde metadata (opcional)
  let bizAperDesde = null;
  let bizAperHasta = null;
  try {
    const bizRes = await BusinessApiClient.getById(businessId);
    const biz    = bizRes?.data?.data ?? bizRes?.data ?? {};
    const meta   = biz.metadata ?? {};
    if (meta.hora_apertura) bizAperDesde = minFromHHMM(meta.hora_apertura);
    if (meta.hora_cierre)   bizAperHasta = minFromHHMM(meta.hora_cierre);
  } catch (_) { /* no bloquear si falla */ }

  // ── Estado ───────────────────────────────────────────────────────────────────
  let modoVista      = 'semana';  // 'semana' | 'dia'
  let todasPlantillas = [];
  let todosLosColabs  = [];
  let colabsVisible   = null;     // Set<id> | null = todos
  let cobFecha        = hoy;
  let cobDesde        = bizAperDesde ?? 8 * 60;
  let cobHasta        = bizAperHasta ?? 22 * 60;
  let cobAperDesde    = bizAperDesde;
  let cobAperHasta    = bizAperHasta;

  const toHHMM = m => m == null ? '' : `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;
  const diaDeISO = iso => { const d = new Date(iso + 'T12:00:00'); return d.getDay() === 0 ? 7 : d.getDay(); };

  // ── HTML base ─────────────────────────────────────────────────────────────
  container.innerHTML = `
    <div class="cob-toolbar">
      <div class="cob-toolbar-row">
        <div class="cob-view-toggle">
          <button class="cob-vt-btn cob-vt-semana active" title="Vista semanal">📅 Semana</button>
          <button class="cob-vt-btn cob-vt-dia" title="Vista diaria">🕐 Día</button>
        </div>
        <div id="cob-dia-opts" style="display:none">
          <label style="display:flex;align-items:center;gap:.4rem">
            Fecha: <input type="date" id="cob-fecha" value="${hoy}" class="form-control form-control-sm">
            <span id="cob-dia-badge" class="cob-dia-badge"></span>
          </label>
          <label style="display:flex;align-items:center;gap:.4rem">
            Rango:
            <input type="time" id="cob-desde" value="${toHHMM(cobDesde)}" class="form-control form-control-sm">–
            <input type="time" id="cob-hasta" value="${toHHMM(cobHasta)}" class="form-control form-control-sm">
          </label>
        </div>
        <label style="display:flex;align-items:center;gap:.4rem">
          Apertura negocio:
          <input type="time" id="cob-aper-desde" value="${toHHMM(cobAperDesde)}" placeholder="10:00" class="form-control form-control-sm" title="Editable en Negocios → Info (metadata.hora_apertura)">–
          <input type="time" id="cob-aper-hasta" value="${toHHMM(cobAperHasta)}" placeholder="21:00" class="form-control form-control-sm" title="Editable en Negocios → Info (metadata.hora_cierre)">
        </label>
      </div>
      <div class="cob-colab-list" id="cob-colab-filter"><em>Cargando…</em></div>
    </div>
    <div id="cob-content"><div class="spinner"></div></div>`;

  const contentEl  = container.querySelector('#cob-content');
  const fechaInput = container.querySelector('#cob-fecha');
  const diaOptsEl  = container.querySelector('#cob-dia-opts');

  // ── Vista semanal: grid tiempo × (día × colaborador) ─────────────────────
  function renderSemana() {
    const porColab = {};
    const colabMeta = {};
    const filtradas = todasPlantillas.filter(p => !colabsVisible || colabsVisible.has(p.colaboradorId));

    for (const p of filtradas) {
      if (!porColab[p.colaboradorId]) {
        porColab[p.colaboradorId] = {};
        colabMeta[p.colaboradorId] = { nombre: p.nombreColaborador, cargo: p.cargoColaborador };
      }
      porColab[p.colaboradorId][p.diaSemana] = p;
    }

    const colabIds = Object.keys(porColab);
    if (!colabIds.length) {
      contentEl.innerHTML = `
        <div class="cob-empty-state">
          <div class="cob-empty-icon">📅</div>
          <p class="cob-empty-title">Sin plantillas asignadas</p>
          <p class="cob-empty-hint">Crea horarios en la pestaña <strong>Plantillas</strong>.</p>
        </div>`;
      return;
    }

    // Rango de tiempo derivado de los horarios reales
    let minT = Infinity, maxT = -Infinity;
    for (const cid of colabIds) {
      for (const p of Object.values(porColab[cid])) {
        const ent = minFromHHMM(p.horaEntrada);
        const sal = minFromHHMM(p.horaSalida);
        if (ent != null) minT = Math.min(minT, ent);
        if (sal != null) maxT = Math.max(maxT, sal);
      }
    }
    if (!isFinite(minT)) minT = cobAperDesde ?? 8 * 60;
    if (!isFinite(maxT)) maxT = cobAperHasta ?? 22 * 60;
    minT = Math.floor(minT / BLOQUE_STEP) * BLOQUE_STEP;
    maxT = Math.ceil(maxT  / BLOQUE_STEP) * BLOQUE_STEP;

    const slots = [];
    for (let t = minT; t < maxT; t += BLOQUE_STEP) slots.push(t);

    // Encabezado fila 1: días (cada uno abarca N columnas de colaboradores)
    const dayHeaders = DIAS_ISO.map(d =>
      `<th class="cob-grid-day-hdr" colspan="${colabIds.length}">${DIA_LABEL[d]}</th>`
    ).join('');

    // Encabezado fila 2: nombre corto de colaborador por día
    const colabSubHeaders = DIAS_ISO.map(d =>
      colabIds.map(cid => {
        const tienePlantilla = !!porColab[cid]?.[d];
        const isLibre = !tienePlantilla;
        const primerNombre = (colabMeta[cid]?.nombre ?? '?').split(' ')[0];
        return `<th class="cob-grid-colab-hdr${isLibre ? ' cob-gc-libre-hdr' : ''}" title="${colabMeta[cid]?.nombre ?? ''}">${primerNombre}${isLibre ? '<br><small class="cob-gc-libre-tag">Libre</small>' : ''}</th>`;
      }).join('')
    ).join('');

    // Filas de tiempo (bloques de 30 min)
    const bodyRows = slots.map(t => {
      const t2 = t + BLOQUE_STEP;
      const fueraAper = cobAperDesde != null && cobAperHasta != null
        && (t2 <= cobAperDesde || t >= cobAperHasta);

      let row = `<tr class="${fueraAper ? 'cob-grid-row-fuera' : ''}">
        <td class="cob-grid-time-cell">
          <span class="cob-gt-t1">${hhmm(t)}</span>
          <span class="cob-gt-t2">${hhmm(t2)}</span>
        </td>`;

      for (const dia of DIAS_ISO) {
        for (const cid of colabIds) {
          const p = porColab[cid]?.[dia];
          if (!p) {
            row += `<td class="cob-gc-libre" title="Día libre"></td>`;
          } else {
            const bcls = bloqueClass(t, p);
            const tooltip = `${colabMeta[cid]?.nombre ?? ''} · ${DIA_LABEL[dia]} · ${hhmm(t)}–${hhmm(t2)}`;
            row += `<td class="cob-gc-cell cob-gc-${bcls}" title="${tooltip}"></td>`;
          }
        }
      }
      row += '</tr>';
      return row;
    }).join('');

    const aperLeyenda = cobAperDesde != null
      ? `<span><span class="cob-ley-dot cob-ley-fuera-aper"></span>Fuera apertura</span>`
      : '';

    contentEl.innerHTML = `
      <div class="cob-grid-outer">
        <table class="cob-grid-table">
          <thead>
            <tr>
              <th class="cob-grid-hora-hdr" rowspan="2">Hora</th>
              ${dayHeaders}
            </tr>
            <tr>${colabSubHeaders}</tr>
          </thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </div>
      <div class="cob-leyenda">
        <span><span class="cob-ley-dot cob-ley-gtrabajo"></span>Trabajo</span>
        <span><span class="cob-ley-dot cob-ley-galmuerzo"></span>Almuerzo</span>
        <span><span class="cob-ley-dot cob-ley-glibre"></span>Día libre</span>
        ${aperLeyenda}
      </div>`;
  }

  // ── Vista diaria (Gantt horizontal) ───────────────────────────────────────
  async function renderDia() {
    contentEl.innerHTML = '<div class="spinner"></div>';
    try {
      const res = await PlantillasApiClient.cobertura(businessId, cobFecha);
      const plantillasDia = res?.data?.data?.horarios ?? res?.data?.horarios ?? [];
      const visibles = plantillasDia.filter(p => !colabsVisible || colabsVisible.has(p.colaboradorId));
      const numBloques = Math.ceil((cobHasta - cobDesde) / BLOQUE_STEP);

      if (!visibles.length) {
        const d   = new Date(cobFecha + 'T12:00:00');
        const iso = d.getDay() === 0 ? 7 : d.getDay();
        contentEl.innerHTML = `
          <div class="cob-empty-state">
            <div class="cob-empty-icon">📅</div>
            <p class="cob-empty-title">Sin horarios para el <strong>${DIA_LABEL[iso] ?? cobFecha}</strong></p>
            <p class="cob-empty-hint">Cambia la fecha o crea plantillas para este día.</p>
          </div>`;
        return;
      }

      // Encabezado (horas)
      const headerCells = Array.from({ length: numBloques }, (_, i) => {
        const m = cobDesde + i * BLOQUE_STEP;
        return `<div class="cg-bloque-hdr">${m % 60 === 0 ? hhmm(m) : ''}</div>`;
      }).join('');

      // Filas (colaboradores)
      const filas = visibles.map(p => {
        const celdas = Array.from({ length: numBloques }, (_, i) => {
          const bMin = cobDesde + i * BLOQUE_STEP;
          const cls  = bloqueClass(bMin, p);
          const huecoClass = bloqueHueco(bMin, cobAperDesde, cobAperHasta, visibles) ? ' cg-hueco' : '';
          return `<div class="cg-bloque ${cls}${huecoClass}" title="${hhmm(bMin)}–${hhmm(bMin+BLOQUE_STEP)}"></div>`;
        }).join('');
        return `<div class="cg-fila">
          <div class="cg-nombre">
            <div class="cg-nom">${p.nombreColaborador || '?'}</div>
            <div class="cg-car">${p.cargoColaborador || ''}</div>
          </div>
          <div class="cg-celdas">${celdas}</div>
        </div>`;
      }).join('');

      contentEl.innerHTML = `
        <div class="cg-gantt">
          <div class="cg-header">
            <div class="cg-nombre-hdr"></div>
            <div class="cg-celdas">${headerCells}</div>
          </div>
          <div class="cg-body">${filas}</div>
        </div>
        <div class="cob-leyenda">
          <span><span class="cob-ley-dot cob-ley-trabajo"></span>Trabajo</span>
          <span><span class="cob-ley-dot cob-ley-almuerzo"></span>Almuerzo</span>
          ${cobAperDesde != null ? '<span><span class="cob-ley-dot cob-ley-hueco"></span>Hueco apertura</span>' : ''}
        </div>`;
    } catch (err) {
      contentEl.innerHTML = `<div class="error-msg">Error: ${err.message ?? err}</div>`;
    }
  }

  function render() {
    if (modoVista === 'semana') renderSemana();
    else renderDia();
  }

  // ── Filtro de colaboradores ──────────────────────────────────────────────
  function buildFiltro() {
    const el = container.querySelector('#cob-colab-filter');
    if (!todosLosColabs.length) { el.innerHTML = ''; return; }
    colabsVisible = colabsVisible ?? new Set(todosLosColabs.map(c => c.id));

    el.innerHTML = `
      <span class="cob-filter-label">Filtro:</span>
      <button class="btn btn-xs btn-ghost" id="cob-all">Todos</button>
      <button class="btn btn-xs btn-ghost" id="cob-none">Ninguno</button>
      ${todosLosColabs.map(c => `
        <label class="cob-colab-check ${colabsVisible.has(c.id) ? 'cob-colab-active' : ''}">
          <input type="checkbox" data-cid="${c.id}" ${colabsVisible.has(c.id) ? 'checked' : ''}>
          ${c.nombreCompleto ?? c.nombre_completo ?? '?'}
        </label>`).join('')}`;

    el.querySelectorAll('input[type=checkbox]').forEach(cb => {
      cb.addEventListener('change', () => {
        if (cb.checked) colabsVisible.add(cb.dataset.cid);
        else colabsVisible.delete(cb.dataset.cid);
        cb.closest('.cob-colab-check').classList.toggle('cob-colab-active', cb.checked);
        render();
      });
    });
    el.querySelector('#cob-all').onclick = () => {
      colabsVisible = new Set(todosLosColabs.map(c => c.id));
      buildFiltro(); render();
    };
    el.querySelector('#cob-none').onclick = () => {
      colabsVisible = new Set();
      buildFiltro(); render();
    };
  }

  // ── Carga inicial ────────────────────────────────────────────────────────
  try {
    const [pRes, cRes] = await Promise.all([
      PlantillasApiClient.list(businessId),
      ColaboradorApiClient.list(businessId, 1, 50, 'activo'),
    ]);
    const rawP = pRes?.data?.data ?? pRes?.data ?? [];
    todasPlantillas = Array.isArray(rawP) ? rawP : [];

    const rawC = cRes?.data?.data?.items ?? cRes?.data?.items ?? cRes?.data?.data ?? cRes?.data ?? [];
    todosLosColabs = Array.isArray(rawC) ? rawC : [];
  } catch (err) {
    contentEl.innerHTML = `<div class="error-msg">Error cargando datos: ${err.message}</div>`;
    return;
  }

  buildFiltro();
  render();

  // ── Eventos toolbar ──────────────────────────────────────────────────────
  container.querySelector('.cob-vt-semana').addEventListener('click', () => {
    modoVista = 'semana';
    container.querySelector('.cob-vt-semana').classList.add('active');
    container.querySelector('.cob-vt-dia').classList.remove('active');
    diaOptsEl.style.display = 'none';
    render();
  });
  container.querySelector('.cob-vt-dia').addEventListener('click', () => {
    modoVista = 'dia';
    container.querySelector('.cob-vt-dia').classList.add('active');
    container.querySelector('.cob-vt-semana').classList.remove('active');
    diaOptsEl.style.display = 'flex';
    // Actualizar badge
    const iso = diaDeISO(cobFecha);
    container.querySelector('#cob-dia-badge').textContent = DIA_LABEL[iso] ?? '';
    render();
  });

  fechaInput?.addEventListener('change', e => {
    cobFecha = e.target.value;
    const iso = diaDeISO(cobFecha);
    container.querySelector('#cob-dia-badge').textContent = DIA_LABEL[iso] ?? '';
    render();
  });
  container.querySelector('#cob-desde')?.addEventListener('change', e => {
    cobDesde = minFromHHMM(e.target.value) ?? cobDesde; render();
  });
  container.querySelector('#cob-hasta')?.addEventListener('change', e => {
    cobHasta = minFromHHMM(e.target.value) ?? cobHasta; render();
  });
  container.querySelector('#cob-aper-desde')?.addEventListener('change', e => {
    cobAperDesde = e.target.value ? minFromHHMM(e.target.value) : null; render();
  });
  container.querySelector('#cob-aper-hasta')?.addEventListener('change', e => {
    cobAperHasta = e.target.value ? minFromHHMM(e.target.value) : null; render();
  });
}

// ---------------------------------------------------------------------------
// Vista principal
// ---------------------------------------------------------------------------

export function HorariosView() {
  const businessId   = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  let currentTab    = 'plantillas';
  let plantillas    = [];
  let colaboradores = [];
  let loading       = false;

  const container = document.createElement('div');
  container.className = 'view-container';

  async function cargarDatos() {
    if (!businessId) return;
    loading = true;
    renderView();

    try {
      const [pRes, cRes] = await Promise.all([
        PlantillasApiClient.list(businessId),
        ColaboradorApiClient.list(businessId, 1, 50, 'activo'),
      ]);
      // Colaboradores: API devuelve ApiResponse<PagedResponse<T>>
      // cRes.data = { success, data: { items: [...], total }, message }
      // cRes.data.data = { items: [...], total }
      // cRes.data.data.items = ColaboradorDto[]
      const rawColabs =
        cRes?.data?.data?.items ??   // ApiResponse<PagedResponse> → wrapping doble
        cRes?.data?.items        ??  // PagedResponse directo
        cRes?.data?.data         ??  // data directo (array)
        cRes?.data               ??  // fallback
        [];
      colaboradores = (Array.isArray(rawColabs) ? rawColabs : [])
        .filter(c => c.estado === 'activo' || c.status === 'activo');

      // Plantillas: puede ser array directo o ApiResponse<array>
      const rawPlantillas =
        pRes?.data?.data?.items ??
        pRes?.data?.data        ??
        pRes?.data              ??
        [];
      plantillas = Array.isArray(rawPlantillas) ? rawPlantillas : [];
    } catch (err) {
      console.error('Error cargando horarios:', err);
    }
    loading = false;
    renderView();
  }

  function renderView() {
    container.innerHTML = `
      <div class="view-header">
        <div class="view-header-top">
          <h2 class="view-title">📅 Horarios</h2>
          <span class="view-subtitle">${businessName || businessId}</span>
        </div>
        <div class="view-actions">
          <button class="btn btn-primary" id="hr-btn-nueva">+ Nueva plantilla</button>
          <a href="#/asistencia" class="btn btn-ghost btn-sm">⏱ Asistencia</a>
        </div>
      </div>

      <div class="tabs">
        <button class="tab-btn ${currentTab === 'plantillas' ? 'active' : ''}" data-tab="plantillas">📋 Plantillas</button>
        <button class="tab-btn ${currentTab === 'cobertura'  ? 'active' : ''}" data-tab="cobertura">📊 Cobertura</button>
        <button class="tab-btn ${currentTab === 'turnos'     ? 'active' : ''}" data-tab="turnos">🔁 Turnos</button>
      </div>

      <div id="hr-tab-content" class="tab-content">
        ${loading ? '<div class="spinner"></div>' : ''}
      </div>`;

    container.querySelectorAll('.tab-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        currentTab = btn.dataset.tab;
        renderView();
        if (currentTab === 'cobertura') {
          const tabEl = container.querySelector('#hr-tab-content');
          loadCobertura(tabEl, businessId);
        }
      });
    });

    container.querySelector('#hr-btn-nueva')?.addEventListener('click', () => {
      showPlantillaModal({ businessId, colaboradores, onSaved: cargarDatos });
    });

    if (!loading) renderTabContent();
  }

  function renderTabContent() {
    const tabEl = container.querySelector('#hr-tab-content');
    if (!tabEl) return;

    if (currentTab === 'plantillas') {
      if (!businessId) {
        tabEl.innerHTML = '<div class="empty-state"><p>Selecciona un negocio primero.</p></div>';
        return;
      }
      tabEl.innerHTML = renderGrillaPlantillas(plantillas, colaboradores);

      // Clic en + para agregar
      tabEl.querySelectorAll('.btn-celda-add').forEach(btn => {
        btn.addEventListener('click', () => {
          const dia = Number(btn.dataset.dia);
          showPlantillaModal({
            businessId, colaboradores,
            colabId:         btn.dataset.colabId,
            colabNombre:     btn.dataset.colabNombre,
            diaSemanaPresel: [dia],
            onSaved:         cargarDatos,
          });
        });
      });

      // Clic en celda existente para editar
      tabEl.querySelectorAll('.celda-horario').forEach(cel => {
        cel.addEventListener('click', () => {
          const pid = cel.dataset.plantillaId;
          const p   = plantillas.find(x => x.id === pid);
          if (!p) return;
          showPlantillaModal({
            businessId, colaboradores,
            colabId:     cel.dataset.colabId,
            colabNombre: cel.dataset.colabNombre,
            plantilla:   p,
            onSaved:     cargarDatos,
          });
        });
      });
    }
    // Turnos: catalogo temprano/tarde + quien alterna (PANEL-HORARIOS-TURNOS-T1). Se repinta tambien tras recargar datos.
    if (currentTab === 'turnos') renderTurnos(tabEl, businessId, { colaboradores });
    // cobertura se renderiza via loadCobertura
  }

  if (businessId) cargarDatos();

  return container;
}
